import React, { useState, useEffect } from 'react';
import useChartData from '../../hooks/useChartData.js';
import { categorizeStrategy, formatStrategyName } from '../../utils/formatters.js';
import './StatusTable.css';

// Helper function to calculate days held from signal age in configuration
function calculateDaysHeld(strategy) {
  // First check if days_held is directly provided
  if (strategy.days_held != null && strategy.days_held !== 0) {
    return strategy.days_held;
  }

  // Try to parse from configuration JSON
  try {
    const config = typeof strategy.configuration === 'string'
      ? JSON.parse(strategy.configuration)
      : strategy.configuration || {};

    // Extract signal age based on current signal type
    if (strategy.signal === 'BUY' && config.buy_signal_age_days != null) {
      return config.buy_signal_age_days;
    } else if (strategy.signal === 'SELL' && config.sell_signal_age_days != null) {
      return config.sell_signal_age_days;
    } else if (strategy.signal === 'HOLD') {
      // For HOLD signals, prefer sell_signal_age_days, fallback to buy_signal_age_days
      if (config.sell_signal_age_days != null) {
        return config.sell_signal_age_days;
      } else if (config.buy_signal_age_days != null) {
        return config.buy_signal_age_days;
      }
    }
  } catch (e) {
    // If parsing fails, fall through to default
    console.error('Failed to parse configuration for', strategy.strategy_type, e);
  }

  // Default to 0 if no signal age data available
  return 0;
}

export default function StatusTable({ ticker }) {
  const { chartData, isLoading } = useChartData(ticker);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  if (isLoading) {
    return <div className="status-table-loading">Loading strategies...</div>;
  }

  if (!chartData || !chartData.strategies) {
    return <div className="status-table-empty">No strategies available</div>;
  }

  // Group strategies by category
  const grouped = {};
  chartData.strategies.forEach(strategy => {
    const category = categorizeStrategy(strategy.strategy_type);
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(strategy);
  });

  const categories = ['WaveTrend', 'Diamond LRO', 'Symmetric LRO', 'Professional Momentum', 'Other'];

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className="status-table">
      <table>
        <thead>
          <tr>
            <th>Strategy</th>
            <th>Status</th>
            <th>Days</th>
            <th>Win%</th>
          </tr>
        </thead>
        <tbody>
          {categories.map(category => {
            if (!grouped[category] || grouped[category].length === 0) return null;

            const strategies = grouped[category];
            const isExpanded = expandedCategories.has(category);

            return (
              <React.Fragment key={category}>
                <tr className="category-header" onClick={() => toggleCategory(category)}>
                  <td colSpan="4">
                    {isExpanded ? '▼' : '▶'} {category} ({strategies.length})
                  </td>
                </tr>
                {isExpanded && strategies.map((strategy, idx) => (
                  <tr key={idx} className="strategy-row">
                    <td title={strategy.entry_description || strategy.full_description || ''}>
                      {formatStrategyName(strategy.strategy_type, strategy.configuration)}
                    </td>
                    <td className={`status-${strategy.signal?.toLowerCase() || 'none'}`}>
                      {strategy.signal || 'No Position'}
                    </td>
                    <td>{calculateDaysHeld(strategy)}</td>
                    <td>{strategy.win_rate ? `${parseFloat(strategy.win_rate).toFixed(0)}%` : '—'}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
